import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * useResizableHeight — manages a draggable panel height with clamping,
 * localStorage persistence, double-click-to-reset, and window-resize
 * clamping.
 *
 * The drag handle normally sits on the panel's bottom edge, so dragging down
 * grows the panel and dragging up shrinks it. A top-edge handle reverses
 * that direction for a panel anchored at the bottom of its parent.
 *
 * Uses Pointer Events (mouse + touch) with pointer capture so the drag
 * continues even if the cursor leaves the handle element.
 *
 * @param opts.initial    Default height in px (default 208)
 * @param opts.min        Minimum height in px   (default 64)
 * @param opts.max        Maximum height in px   (default 800)
 * @param opts.storageKey If set, persist/restore height via localStorage
 * @param opts.minPeer    Minimum height to reserve for the neighbouring
 *                        panel when the window shrinks (default 240)
 * @param opts.handleSide Panel edge containing the drag handle (default
 *                        "bottom"); a "top" handle inverts the drag delta
 */
export function useResizableHeight(opts: {
  initial?: number;
  min?: number;
  max?: number;
  storageKey?: string;
  minPeer?: number;
  handleSide?: "bottom" | "top";
}) {
  const min = opts.min ?? 64;
  const max = opts.max ?? 800;
  const initial = opts.initial ?? 208;
  const minPeer = opts.minPeer ?? 240;
  const storageKey = opts.storageKey;
  const handleSide = opts.handleSide ?? "bottom";

  const clampInitialHeight = useCallback((value: number) => {
    const maxAllowed = typeof window === "undefined"
      ? max
      : window.innerHeight - minPeer;
    const maxHeight = maxAllowed < min
      ? min
      : Math.min(max, maxAllowed);
    return Math.max(min, Math.min(value, maxHeight));
  }, [max, min, minPeer]);

  const readInitialHeight = useCallback(() => {
    if (storageKey && typeof window !== "undefined") {
      const saved = window.localStorage.getItem(storageKey);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!Number.isNaN(parsed)) {
          return clampInitialHeight(parsed);
        }
      }
    }
    return clampInitialHeight(initial);
  }, [clampInitialHeight, initial, storageKey]);

  const [height, setHeight] = useState<number>(readInitialHeight);
  const heightRef = useRef(height);
  heightRef.current = height;

  // A single resize controller can serve multiple mutually-exclusive panels.
  // When its active panel changes, restore that panel's own persisted height
  // before paint rather than carrying the previous panel's height across.
  const configKey = `${storageKey ?? ""}\u0000${initial}\u0000${min}\u0000${max}\u0000${minPeer}`;
  const previousConfigKey = useRef(configKey);
  useLayoutEffect(() => {
    if (previousConfigKey.current === configKey) return;
    previousConfigKey.current = configKey;
    setHeight(readInitialHeight());
  }, [configKey, readInitialHeight]);

  const configRef = useRef({ initial, min, max, minPeer, storageKey, handleSide });
  configRef.current = { initial, min, max, minPeer, storageKey, handleSide };

  // ── Drag state (ref so pointermove handler stays stable) ──────────────
  const dragState = useRef<{ startY: number; startHeight: number } | null>(
    null,
  );

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    // Keep the configured cap as the drag origin. The panel can render
    // shorter than that cap when its content does not fill the available
    // height, so its bounding rect is not a reliable source here.
    dragState.current = { startY: e.clientY, startHeight: heightRef.current };
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragState.current) return;
    const delta = e.clientY - dragState.current.startY;
    const {
      min: currentMin,
      max: currentMax,
      minPeer: currentMinPeer,
      handleSide: currentHandleSide,
    } = configRef.current;
    const newHeight = dragState.current.startHeight
      + (currentHandleSide === "top" ? -delta : delta);
    const maxAllowed = window.innerHeight - currentMinPeer;
    const maxHeight = maxAllowed < currentMin
      ? currentMin
      : Math.min(currentMax, maxAllowed);
    setHeight(Math.max(currentMin, Math.min(newHeight, maxHeight)));
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragState.current) return;
    dragState.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    // Persist via functional update so we read the latest height.
    const currentStorageKey = configRef.current.storageKey;
    if (currentStorageKey) {
      setHeight((h) => {
        try {
          window.localStorage.setItem(currentStorageKey, String(h));
        } catch {
          /* localStorage may be unavailable (private mode) — ignore */
        }
        return h;
      });
    }
  }, []);

  // Double-click the handle to reset to initial height.
  const onDoubleClick = useCallback(() => {
    setHeight(configRef.current.initial);
  }, []);

  // ── Window-resize clamping ─────────────────────────────────────────────
  // If the viewport shrinks so that height + minPeer > window.innerHeight,
  // shrink the section to keep at least `minPeer` px for its neighbour.
  useEffect(() => {
    const handleResize = () => {
      const { min: currentMin, minPeer: currentMinPeer } = configRef.current;
      const maxAllowed = window.innerHeight - currentMinPeer;
      setHeight((h) => {
        if (maxAllowed < currentMin) return currentMin;
        return Math.min(h, maxAllowed);
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return useMemo(
    () => ({ height, onPointerDown, onPointerMove, onPointerUp, onDoubleClick }),
    [height, onDoubleClick, onPointerDown, onPointerMove, onPointerUp],
  );
}
